import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";

import { apiClient } from "../api/client";
import { Card, Page } from "../layout/Page";
import {
  projectApi,
  selectedProjectId,
  type EditableProject,
  type ProjectSummary,
} from "../workflow";
import { CatalogWorkspace } from "../../features/catalog";
import { CurveImportWorkspace } from "../../features/curve_import";
import { ExportWorkspace } from "../../features/exports";
import { FleetWorkspace, fleetApi } from "../../features/fleet";
import { NetworkFeature } from "../../features/network";
import {
  OptimizationWorkspace,
  type OptimizationSubmission,
  type OptimizationRunRecord,
} from "../../features/optimization";
import {
  ResultsWorkspace,
  type SavedResultsSnapshot,
} from "../../features/results";
import {
  ScenariosFeature,
  type ScenarioDraftInput,
  type ScenarioProjectSettings,
} from "../../features/scenarios";
import { SettingsWorkspace } from "../../features/settings";
import { SimulationWorkspace } from "../../features/simulation";

export { ProjectsPage } from "../../features/projects";

export function CatalogPage() {
  return <CatalogWorkspace />;
}
export function FleetPage() {
  const [params] = useSearchParams();
  const entryId = params.get("library_entry_id");
  const templateId = params.get("pump_template_id");
  const revisionId = params.get("pump_template_revision_id");
  const variantId = params.get("pump_variant_id");
  const reviewedTarget = entryId && templateId && revisionId && variantId
    ? { entryId, templateId, revisionId, variantId } : null;
  return <FleetWorkspace reviewedTarget={reviewedTarget} />;
}
export function CurvesPage() {
  return <CurveImportWorkspace />;
}
export function SettingsPage() {
  return <SettingsWorkspace />;
}

function useProjectSelection() {
  const projects = useQuery({
    queryKey: ["flow-wizard", "projects"],
    queryFn: projectApi.list,
  });
  const stored = selectedProjectId();
  const selected = projects.data?.find((item) => item.id === stored) ?? null;
  return { projects, selected };
}

interface GuardProps {
  allowReadOnly?: boolean;
  children: (summary: ProjectSummary) => ReactNode;
}
function ProjectGuard({ allowReadOnly = false, children }: GuardProps) {
  const { projects, selected } = useProjectSelection();
  if (projects.isPending)
    return (
      <Page title="Loading project">
        <p role="status">Loading project…</p>
      </Page>
    );
  if (projects.isError)
    return (
      <Page title="Project unavailable">
        <p role="alert">{projects.error.message}</p>
      </Page>
    );
  if (selected === null)
    return (
      <Page title="Select a project">
        <Card>
          <p>Choose a project first.</p>
          <Link to="/projects">Open Projects</Link>
        </Card>
      </Page>
    );
  if (selected.read_only && !allowReadOnly)
    return <ReadOnlyProject summary={selected} />;
  return children(selected);
}

function ReadOnlyProject({ summary }: { summary: ProjectSummary }) {
  const portable = useQuery({
    queryKey: ["flow-wizard", "portable-project", summary.id],
    queryFn: () => projectApi.getPortable(summary.id),
  });

  return (
    <Page title={`${summary.name} · read-only`}>
      <Card title="Portable project">
        <p>Results and exports are available. Editing is disabled.</p>
        <div className="button-row">
          <Link className="button button--primary" to="/results">
            Open results
          </Link>
          <Link className="button button--secondary" to="/exports">
            Open exports
          </Link>
        </div>
      </Card>
      <details className="card">
        <summary>Record details</summary>
        {portable.isPending ? (
          <p role="status">Loading…</p>
        ) : portable.isError ? (
          <p role="alert">{portable.error.message}</p>
        ) : (
          <p>
            Snapshot hash <code>{String(portable.data.equivalence_sha256)}</code>
          </p>
        )}
        <p>The imported snapshot is authoritative.</p>
      </details>
    </Page>
  );
}

export function NetworkPage() {
  return (
    <ProjectGuard>
      {(summary) => <EditableNetwork summary={summary} />}
    </ProjectGuard>
  );
}
function EditableNetwork({ summary }: { summary: ProjectSummary }) {
  const queryClient = useQueryClient();
  const project = useQuery({
    queryKey: ["flow-wizard", "project", summary.id],
    queryFn: () => projectApi.get(summary.id),
  });
  const validation = useQuery({
    queryKey: ["flow-wizard", "project-validation", summary.id],
    queryFn: () => projectApi.validate(summary.id),
  });
  if (project.isPending)
    return (
      <Page title="Loading network">
        <p role="status">Loading network…</p>
      </Page>
    );
  if (project.isError)
    return (
      <Page title="Network unavailable">
        <p role="alert">{project.error.message}</p>
      </Page>
    );
  const recordSavedProject = async (saved: EditableProject) => {
    queryClient.setQueryData(["flow-wizard", "project", summary.id], saved);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["flow-wizard", "projects"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["flow-wizard", "results", summary.id],
      }),
      queryClient.invalidateQueries({
        queryKey: ["flow-wizard", "project-validation", summary.id],
      }),
    ]);
  };
  return (
    <>
      {validation.isError ? (
        <p role="alert">
          Project validation unavailable: {validation.error.message}
        </p>
      ) : null}
      <NetworkFeature
        key={project.data.id}
        initialProject={project.data}
        onProjectSaved={async (saved) => {
          await recordSavedProject(saved as EditableProject);
        }}
        onTerrainPreview={(current, maximumSpacingM) =>
          projectApi.terrainTargets(current.id, maximumSpacingM)
        }
        onOpenMeteoTerrainRefresh={async (current, maximumSpacingM) => {
          const result = await projectApi.refreshOpenMeteoTerrain(
            current,
            maximumSpacingM,
          );
          await recordSavedProject(result.project as EditableProject);
          return result;
        }}
        onUsgsTerrainRefresh={async (current, maximumSpacingM) => {
          const result = await projectApi.refreshUsgsTerrain(
            current,
            maximumSpacingM,
          );
          await recordSavedProject(result.project as EditableProject);
          return result;
        }}
        onManualTerrainRefresh={async (current, values) => {
          const result = await projectApi.refreshManualTerrain(current, values);
          await recordSavedProject(result.project as EditableProject);
          return result;
        }}
        onTopologyCommand={async (command, current) => {
          const saved = await projectApi.topology(current, command);
          await recordSavedProject(saved);
          return saved;
        }}
        {...(validation.data === undefined
          ? {}
          : { validationIssues: validation.data.issues })}
      />
    </>
  );
}

function newScenario(project: EditableProject): ScenarioDraftInput {
  const identifier =
    globalThis.crypto?.randomUUID?.() ?? `scenario-${Date.now()}`;
  return {
    fleet_settings: [],
    id: identifier,
    kind: "normal",
    name: "Normal operation",
    required: true,
    sinks: project.nodes
      .filter((item) => item.node_type === "sink")
      .map((item) => ({
        minimum_residual_pressure_gauge_pa: null,
        job_requirement: null,
        required_flow_m3_s: null,
        sink_node_id: item.id,
      })),
    sources: project.nodes
      .filter((item) => item.node_type === "source")
      .map((item) => ({
        hydraulic_head_m: null,
        maximum_withdrawal_flow_m3_s: null,
        source_node_id: item.id,
      })),
    water: {
      density_kg_m3: 1000,
      kinematic_viscosity_m2_s: 1.13e-6,
      temperature_k: 288.705555555556,
      vapor_pressure_absolute_pa: 1768,
    },
    weight: 1,
  };
}

export function ScenariosPage() {
  return (
    <ProjectGuard>
      {(summary) => <EditableScenarios summary={summary} />}
    </ProjectGuard>
  );
}
function EditableScenarios({ summary }: { summary: ProjectSummary }) {
  const queryClient = useQueryClient();
  const project = useQuery({
    queryKey: ["flow-wizard", "project", summary.id],
    queryFn: () => projectApi.get(summary.id),
  });
  const fleet = useQuery({
    queryKey: ["flow-wizard", "scenario-fleet"],
    queryFn: async () => {
      const page = await fleetApi.listUnits("", "exclude");
      return Promise.all(
        page.items.map(async (item) => {
          const details = await fleetApi.getUnit(item.id);
          return {
            availability: details.availability,
            id: details.id,
            maximum_engine_speed_rpm: details.maximum_engine_speed_rpm,
            name: details.name,
            physical_identifier: details.physical_identifier,
            template_name: item.template_name,
          };
        }),
      );
    },
  });
  if (project.isPending || fleet.isPending)
    return (
      <Page title="Loading scenarios">
        <p role="status">Loading scenarios…</p>
      </Page>
    );
  if (project.isError)
    return (
      <Page title="Scenarios unavailable">
        <p role="alert">{project.error.message}</p>
      </Page>
    );
  if (fleet.isError)
    return (
      <Page title="Scenarios unavailable">
        <p role="alert">Fleet unavailable: {fleet.error.message}</p>
      </Page>
    );
  const document = project.data;
  const initial = document.scenarios[0] ?? newScenario(document);
  const settings: ScenarioProjectSettings = {
    ...document.safety_settings,
    headloss_model: document.headloss_model,
    safety_acknowledged: document.safety_settings_acknowledged_at !== null,
    terrain_accuracy_acknowledged:
      document.terrain_accuracy_warning_acknowledged_at !== null,
  };
  async function save(
    scenario: ScenarioDraftInput,
    next: ScenarioProjectSettings,
  ) {
    const now = new Date().toISOString();
    const existing = document.scenarios.find((item) => item.id === scenario.id);
    const complete = {
      ...(existing ?? {
        schema_version: "1.0",
        created_at: now,
        updated_at: now,
        description: null,
      }),
      ...scenario,
      updated_at: now,
    };
    const saved = await projectApi.save({
      ...document,
      scenarios: [
        ...document.scenarios.filter((item) => item.id !== scenario.id),
        complete,
      ],
      headloss_model: next.headloss_model,
      safety_settings: {
        component_pressure_margin_fraction:
          next.component_pressure_margin_fraction,
        driver_power_reserve_fraction: next.driver_power_reserve_fraction,
        minimum_profile_pressure_gauge_pa:
          next.minimum_profile_pressure_gauge_pa,
        npsh_margin_m: next.npsh_margin_m,
      },
      safety_settings_acknowledged_at: next.safety_acknowledged ? now : null,
      terrain_accuracy_warning_acknowledged_at:
        next.terrain_accuracy_acknowledged ? now : null,
      updated_at: now,
    });
    queryClient.setQueryData(["flow-wizard", "project", summary.id], saved);
    await queryClient.invalidateQueries({
      queryKey: ["flow-wizard", "projects"],
    });
  }
  return (
    <ScenariosFeature
      key={document.id}
      existingScenarios={document.scenarios}
      fleetUnits={fleet.data}
      initialDraft={initial}
      initialSettings={settings}
      onSave={save}
      sourceNodes={document.nodes.filter((item) => item.node_type === "source")}
      sinkNodes={document.nodes.filter((item) => item.node_type === "sink")}
    />
  );
}

export function SimulationPage() {
  return (
    <ProjectGuard>
      {(summary) => <ProjectSimulation summary={summary} />}
    </ProjectGuard>
  );
}
function ProjectSimulation({ summary }: { summary: ProjectSummary }) {
  const project = useQuery({
    queryKey: ["flow-wizard", "project", summary.id],
    queryFn: () => projectApi.get(summary.id),
  });
  const fleet = useQuery({
    queryKey: ["flow-wizard", "simulation-fleet"],
    queryFn: async () => {
      const page = await fleetApi.listUnits("", "exclude");
      return Promise.all(
        page.items.map(async (item) => {
          const details = await fleetApi.getUnit(item.id);
          return {
            availability: details.availability,
            id: details.id,
            maximum_engine_speed_rpm: details.maximum_engine_speed_rpm,
            minimum_engine_speed_rpm: details.minimum_engine_speed_rpm,
            name: details.name,
            optimization_eligible: item.optimization_eligible,
            physical_identifier: details.physical_identifier,
            template_name: item.template_name,
          };
        }),
      );
    },
  });
  if (project.isPending || fleet.isPending)
    return (
      <Page title="Loading simulation">
        <p role="status">Loading simulation…</p>
      </Page>
    );
  if (project.isError)
    return (
      <Page title="Simulation unavailable">
        <p role="alert">{project.error.message}</p>
      </Page>
    );
  if (fleet.isError)
    return (
      <Page title="Simulation unavailable">
        <p role="alert">
          Fleet unavailable: {fleet.error.message}
        </p>
      </Page>
    );
  return (
    <SimulationWorkspace
      candidateSites={project.data.candidate_pump_sites}
      fleetUnits={fleet.data}
      projectId={project.data.id}
      projectRevision={project.data.revision}
      scenarios={project.data.scenarios}
    />
  );
}

export function OptimizationPage() {
  return (
    <ProjectGuard>
      {(summary) => <ProjectOptimization summary={summary} />}
    </ProjectGuard>
  );
}
function ProjectOptimization({ summary }: { summary: ProjectSummary }) {
  const project = useQuery({
    queryKey: ["flow-wizard", "project", summary.id],
    queryFn: () => projectApi.get(summary.id),
  });
  const submission = useQuery({
    enabled: project.data !== undefined,
    queryKey: [
      "flow-wizard",
      "optimization-submission",
      summary.id,
      project.data?.revision ?? null,
    ],
    queryFn: () => {
      const current = project.data;
      if (current === undefined) {
        throw new Error("Project inputs are not ready.");
      }
      return apiClient.post<OptimizationSubmission>(
        "/workflow/optimization-submission",
        {
          json: {
            project_id: current.id,
            expected_project_revision: current.revision,
            mode: "preview",
            time_budget_s: 900,
            deterministic_seed: 0,
            speed_increment_rpm: 25,
            site_orientation_policies: [],
            allow_scenario_unit_disabling: false,
          },
        },
      );
    },
    retry: false,
  });

  if (project.isPending) {
    return (
      <Page title="Optimization">
        <p role="status">Loading project…</p>
      </Page>
    );
  }
  if (project.isError) {
    return (
      <Page title="Optimization unavailable">
        <p role="alert">{project.error.message}</p>
      </Page>
    );
  }
  if (submission.isPending) {
    return (
      <Page title="Optimization">
        <p role="status">Checking inputs…</p>
      </Page>
    );
  }
  if (submission.isError) {
    return (
      <Page title="Optimization">
        <Card title="Needs setup">
          <p role="alert">{submission.error.message}</p>
        </Card>
      </Page>
    );
  }

  return (
    <Page title="Optimization">
      <OptimizationWorkspace
        key={submission.data.request.input_hash}
        initialRequest={submission.data.request}
      />
    </Page>
  );
}

interface PortableResultsResponse {
  read_only_import: boolean;
  records: Array<{
    id: string;
    input_hash: string;
    stale: boolean;
    stale_reason: string | null;
  }>;
  snapshot: PortableSnapshot;
}
interface PortableSnapshot {
  captured_at: string;
  equivalence_sha256: string;
  project: {
    id: string;
    name: string;
    nodes: Array<{ id: string; name: string }>;
    links: Array<{ id: string; name: string }>;
    candidate_pump_sites: Array<{ id: string; name: string }>;
    scenarios: Array<{ id: string; name: string }>;
  };
  fleet_units: Array<{ id: string; physical_identifier: string }>;
  simulations: Array<{
    saved_at: string;
    request_sha256: string;
    result_sha256: string;
    snapshot_sha256: string;
    result: Record<string, unknown>;
    request: { input_hash: string };
  }>;
  optimizations: Array<{
    saved_at: string;
    run_sha256: string;
    run: OptimizationRunRecord;
  }>;
}
function value(check: Record<string, unknown>, stem: string): number | null {
  const suffix: Record<string, string> = {
    flow: "flow_m3_s",
    pressure: "pressure_gauge_pa",
    head: "head_m",
    power: "power_w",
    speed: "speed_rpm",
    fraction: "fraction",
  };
  const measurement = String(check.measurement);
  const candidate = check[`${stem}_${suffix[measurement] ?? ""}`];
  return typeof candidate === "number" ? candidate : null;
}
function unit(measurement: string): string {
  return (
    (
      {
        flow: "m³/s",
        pressure: "Pa gauge",
        head: "m",
        power: "W",
        speed: "rpm",
        fraction: "fraction",
        state: "state",
      } as Record<string, string>
    )[measurement] ?? measurement
  );
}
// eslint-disable-next-line react-refresh/only-export-components -- Pure adapter has focused regression coverage.
export function adaptResults(snapshot: PortableSnapshot): SavedResultsSnapshot {
  const nodes = new Map(
    snapshot.project.nodes.map((item) => [item.id, item.name]),
  );
  const links = new Map(
    snapshot.project.links.map((item) => [item.id, item.name]),
  );
  const sites = new Map(
    snapshot.project.candidate_pump_sites.map((item) => [item.id, item.name]),
  );
  const scenarios = new Map(
    snapshot.project.scenarios.map((item) => [item.id, item.name]),
  );
  const fleet = new Map(
    snapshot.fleet_units.map((item) => [item.id, item.physical_identifier]),
  );
  return {
    captured_at: snapshot.captured_at,
    equivalence_sha256: snapshot.equivalence_sha256,
    project_id: snapshot.project.id,
    project_name: snapshot.project.name,
    simulations: snapshot.simulations.map((saved) => {
      const result = saved.result;
      const scenarioId = String(result.scenario_id);
      return {
        id: String(result.id),
        captured_at: saved.saved_at,
        state:
          result.state as SavedResultsSnapshot["simulations"][number]["state"],
        converged: Boolean(result.converged),
        feasible: Boolean(result.feasible),
        request_sha256: saved.request_sha256,
        result_sha256: saved.result_sha256,
        snapshot_sha256: saved.snapshot_sha256,
        scenario_id: scenarioId,
        scenario_name: scenarios.get(scenarioId) ?? scenarioId,
        solver_versions: result.solver_versions as Array<{
          component: string;
          version: string;
        }>,
        failures: result.failures as Array<{ code: string; message: string }>,
        nodes: (result.node_results as Array<Record<string, unknown>>).map(
          (item) => ({
            node_id: String(item.node_id),
            node_name: nodes.get(String(item.node_id)) ?? String(item.node_id),
            demand_flow_m3_s: Number(item.demand_flow_m3_s),
            hydraulic_head_m: Number(item.hydraulic_head_m),
            pressure_gauge_pa: Number(item.pressure_gauge_pa),
          }),
        ),
        links: (result.link_results as Array<Record<string, unknown>>).map(
          (item) => ({
            link_id: String(item.link_id),
            link_name: links.get(String(item.link_id)) ?? String(item.link_id),
            flow_m3_s: Number(item.flow_m3_s),
            velocity_m_s: Number(item.velocity_m_s),
            headloss_m: Number(item.headloss_m),
          }),
        ),
        pumps: (
          result.pump_operating_points as Array<Record<string, unknown>>
        ).map((item) => ({
          fleet_unit_id: String(item.fleet_unit_id),
          physical_identifier:
            fleet.get(String(item.fleet_unit_id)) ?? String(item.fleet_unit_id),
          candidate_site_id: String(item.candidate_site_id),
          candidate_site_name:
            sites.get(String(item.candidate_site_id)) ??
            String(item.candidate_site_id),
          engine_speed_rpm: Number(item.engine_speed_rpm),
          pump_speed_rpm: Number(item.pump_speed_rpm),
          flow_m3_s: Number(item.flow_m3_s),
          head_gain_m: Number(item.head_gain_m),
          brake_power_w:
            typeof item.brake_power_w === "number" ? item.brake_power_w : null,
          npsha_m: typeof item.npsha_m === "number" ? item.npsha_m : null,
          npshr_m: typeof item.npshr_m === "number" ? item.npshr_m : null,
          npsh_margin_m:
            typeof item.npsh_margin_m === "number" ? item.npsh_margin_m : null,
        })),
        constraints: (
          result.constraint_checks as Array<Record<string, unknown>>
        ).map((item) => ({
          type: String(item.constraint_type),
          state: item.state as "failed" | "not_evaluated" | "passed",
          entity_id: item.entity_id === null ? null : String(item.entity_id),
          measurement: String(item.measurement),
          actual: value(item, "actual"),
          limit: value(item, "limit"),
          margin: value(item, "margin"),
          unit: unit(String(item.measurement)),
          message: String(item.message),
        })),
      };
    }),
    optimizations: snapshot.optimizations.map(({ run, run_sha256, saved_at }) => ({
      run,
      run_sha256,
      saved_at,
    })),
  };
}

export function ResultsPage() {
  return (
    <ProjectGuard allowReadOnly>
      {(summary) => <ProjectResults summary={summary} />}
    </ProjectGuard>
  );
}
function ProjectResults({ summary }: { summary: ProjectSummary }) {
  const results = useQuery({
    queryKey: ["flow-wizard", "results", summary.id],
    queryFn: () =>
      apiClient.get<PortableResultsResponse>(`/results/projects/${summary.id}`),
  });
  if (results.isPending)
    return (
      <Page title="Loading saved results">
        <p role="status">Loading results…</p>
      </Page>
    );
  if (results.isError)
    return (
      <Page title="No saved results">
        <Card>
          <p role="alert">{results.error.message}</p>
          <p>Run Simulation or Optimization, or import a project.</p>
        </Card>
      </Page>
    );
  const stale = results.data.records.filter((item) => item.stale);
  return (
    <>
      {stale.length === 0 ? null : (
        <div className="notice" role="alert">
          <strong>Older results</strong>
          <span>
            {stale.length} saved {stale.length === 1
              ? "run uses earlier inputs."
              : "runs use earlier inputs."}
          </span>
        </div>
      )}
      <ResultsWorkspace snapshot={adaptResults(results.data.snapshot)} />
    </>
  );
}

export function ExportsPage() {
  const { projects, selected } = useProjectSelection();
  if (projects.isPending)
    return (
      <Page title="Loading projects">
        <p role="status">Loading exports…</p>
      </Page>
    );
  if (projects.isError)
    return (
      <Page title="Projects unavailable">
        <p role="alert">{projects.error.message}</p>
      </Page>
    );
  return <ExportWorkspace projectId={selected?.id ?? null} />;
}
